import * as Font from 'expo-font';
import Fonts from 'src/core/utils/Fonts';
import { useState, useEffect } from 'react';
import { useAuthStore } from 'src/domain/stores/useAuthStore';
import { useServicesStore } from 'src/domain/stores/useServicesStore';
import { loadMainStackNavigator } from 'src/presentation/navigation/navigators/RootNavigator';
import barlowBold from 'assets/fonts/Barlow-Bold.ttf';
import barlowItalic from 'assets/fonts/Barlow-Italic.ttf';
import barlowMedium from 'assets/fonts/Barlow-Medium.ttf';
import barlowRegular from 'assets/fonts/Barlow-Regular.ttf';
import ptSerifRegular from 'assets/fonts/PTSerif-Regular.ttf';

// expo-font gives up on a slow font on web with this error, after which the browser falls back to a system font.
const FONT_LOAD_TIMEOUT = /^\d+ms timeout exceeded$/;

const isFontLoadTimeout = (error: unknown): boolean => error instanceof Error && FONT_LOAD_TIMEOUT.test(error.message);

export default function useCachedResources() {
	const [isLoadingComplete, setLoadingComplete] = useState(false);
	const { init: initAuthStore } = useAuthStore();

	useEffect(() => {
		const reportFailure = (error: unknown) =>
			useServicesStore.getState().captureEvent({ message: 'useCachedResources failed', extra: { error } });

		const fonts = Font.loadAsync({
			[Fonts.ptSerif]: ptSerifRegular,
			[Fonts.barlow]: barlowRegular,
			[Fonts.barlowMedium]: barlowMedium,
			[Fonts.barlowBold]: barlowBold,
			[Fonts.barlowItalic]: barlowItalic,
		}).catch((error: unknown) => {
			if (!isFontLoadTimeout(error)) {
				reportFailure(error);
			}
		});
		const mainStackNavigator = initAuthStore()
			.then(() => (useAuthStore.getState().isAuthenticated ? loadMainStackNavigator() : null))
			.catch(reportFailure);

		Promise.all([fonts, mainStackNavigator])
			.catch(reportFailure)
			.finally(() => setLoadingComplete(true));
	}, [initAuthStore]);

	return isLoadingComplete;
}
